import { TEntityMilestone, TFeatureWorkflowInstance } from "entity-app/models";
import { useRef } from "react";
import { Button, Tooltip } from "antd";
import DatePickerWithIcon from "components/date-picker-with-icon/date-picker-with-icon";
import { DateUtils } from "utils/dateutils";
import ProgressIndicatorCircle from "components/date-block/progress-indicator-circle";
import { getDaysText } from "utils/utils";
import {
  CalendarOutlined,
  CarryOutOutlined,
  EditOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import { ErrorMessages, EWorkflowStatusDataBlock } from "constants/index";
import { InputEditDataType } from "popups/rename-model/rename-popup";
import { RiskLevelType } from "components/date-block/models";
import CriticalActivityTag from "pages/schedule/schedule-page/critical-activity-tag";
import {
  TDateBlockPermissions,
  TGoverningSource,
  TWidthCalculation
} from "../../models";

export default function TimelineIconsOnly(props: {
  milestones: Array<TEntityMilestone>;
  currentMilestone: TEntityMilestone | undefined;
  widthCal: TWidthCalculation;
  featureWorkflowInstance: TFeatureWorkflowInstance;
  applyWorkflowChange: (data: any) => Promise<void>;
  showFloat: boolean;
  setIsRenameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRenameDataInput: React.Dispatch<React.SetStateAction<InputEditDataType>>;
  governingSource: TGoverningSource | undefined;
  permissions: TDateBlockPermissions;
  entityHeaders: any;
}) {
  const {
    milestones,
    currentMilestone,
    widthCal,
    featureWorkflowInstance,
    applyWorkflowChange,
    showFloat,
    governingSource,
    setIsRenameModalOpen,
    setRenameDataInput,
    permissions,
    entityHeaders
  } = props;
  const { length } = milestones;

  const rectWidth = 48;
  const circleWidth = 24;
  const lineWidth = (widthCal.timelineWidth - circleWidth - rectWidth) / 2;
  const leftMargin = (widthCal.milestonesWidth - circleWidth) / 2;

  const childRefPlannedDate = useRef<any>();
  const renderExtraFooterDiv = (
    <div className="flex justify-center py-2">
      <Button
        disabled={!permissions.canEditPlannedDate}
        onClick={() => {
          childRefPlannedDate.current?.close();
          applyWorkflowChange({ manual_entry_date: null });
        }}
        data-testid="index-button-clear-date"
      >
        Clear Date
      </Button>
    </div>
  );

  const allActualSubmitted = milestones.every((m) => m.actual_date);

  const isWorkflowStarted =
    featureWorkflowInstance.workflow_status !==
    EWorkflowStatusDataBlock.WF_NOT_STARTED;

  const isWorkflowCompleted =
    featureWorkflowInstance?.workflow_status ===
    EWorkflowStatusDataBlock.WF_COMPLETED;

  const plannedFloat = featureWorkflowInstance.pre_WF_start_float;
  const actualFloat = featureWorkflowInstance.effective_float;

  const floatBlock = (
    <div className="flex items-center">
      <div className="bl-line" style={{ width: widthCal.lastLineWidth }} />
      <div className="bl-rect bg-ciq-gray-350">
        <div className="absolute w-52 h-52 -left-20 top-10 flex-col space-y-3 text-xs text-ciq-gray-800">
          {showFloat && (
            <div className="w-full bg-ciq-neutral-50 p-1">
              <div className="flex justify-between p-1 items-center">
                <div>Planned Float</div>
                <div className="font-semibold space-x-1 flex items-center">
                  <div>
                    {plannedFloat} {getDaysText(plannedFloat)}
                  </div>
                  {permissions.canEditDurations && !isWorkflowStarted && (
                    <EditOutlined
                      className="pl-1"
                      onClick={() => {
                        setRenameDataInput({
                          value: plannedFloat,
                          id: featureWorkflowInstance.id,
                          lable: "Float",
                          type: "text",
                          identityName: "WorkflowInstanceFloat"
                        });
                        setIsRenameModalOpen(true);
                      }}
                    />
                  )}
                </div>
              </div>
              <div className="flex justify-between p-1 items-center">
                <div>Actual Float</div>
                <div
                  className={
                    featureWorkflowInstance.risk_level === RiskLevelType.High
                      ? "date-block-risk-high font-semibold"
                      : "font-semibold"
                  }
                >
                  {actualFloat != null
                    ? `${actualFloat} ${getDaysText(actualFloat)}`
                    : " - "}
                </div>
              </div>
            </div>
          )}
          <div className="w-full p-1 bg-ciq-neutral-50">
            <div className="flex justify-between p-1 space-x-1 items-baseline">
              <div className="break-normal">
                {entityHeaders?.final_deadline_date ?? "Deadline"}
              </div>
              <div className="font-semibold flex space-x-1 items-center">
                {featureWorkflowInstance.final_deadline_date ? (
                  <div className="w-20">
                    {DateUtils.format(
                      featureWorkflowInstance.final_deadline_date
                    )}
                  </div>
                ) : (
                  <div className="px-1">-</div>
                )}
                {featureWorkflowInstance.final_deadline_date && (
                  <Tooltip
                    title={
                      governingSource ? (
                        <div className="p-2">
                          <div className="text-ciq-gray-440">Source:</div>
                          <div>{governingSource.type}</div>
                          <div className="pb-2">({governingSource.name})</div>
                          {governingSource.type === "Linked Activity" && (
                            <CriticalActivityTag />
                          )}
                        </div>
                      ) : (
                        ""
                      )
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                )}

                <div className="mb-1 flex">
                  <DatePickerWithIcon
                    title={
                      !permissions.canEditPlannedDate
                        ? ErrorMessages.PermissionNotGranted
                        : "Set date to be no later than"
                    }
                    disabledDate={() => {
                      return (
                        !permissions.canEditPlannedDate ||
                        allActualSubmitted ||
                        isWorkflowCompleted
                      );
                    }}
                    value={
                      featureWorkflowInstance.manual_entry_date
                        ? DateUtils.dateTimeObj(
                            featureWorkflowInstance.manual_entry_date
                          )
                        : undefined
                    }
                    icon={
                      featureWorkflowInstance.manual_entry_date ? (
                        <CarryOutOutlined />
                      ) : (
                        <CalendarOutlined />
                      )
                    }
                    onChange={(date) => {
                      if (date) {
                        const saveDate =
                          DateUtils.formatDateWithLunchTime(date);
                        const set = { manual_entry_date: saveDate };
                        applyWorkflowChange(set);
                      }
                    }}
                    ref={childRefPlannedDate}
                    renderExtraFooter={() => {
                      return featureWorkflowInstance.manual_entry_date
                        ? renderExtraFooterDiv
                        : undefined;
                    }}
                  />
                  {governingSource?.name === "Manually Set Date" && (
                    <div className="-ml-0.5">*</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <div className="flex h-10" data-testid="timeline-iconsonly-root">
      <div className="shrink-0" style={{ width: leftMargin }} />
      {milestones.map((m, index) => {
        const isNextMileStone = length !== index + 1;
        const isOffest = m.feature_workflow_instance_offsets_as_src?.length > 0;

        return (
          <div
            key={`TimelineIconsOnly_${m.id}`}
            className="flex relative items-center"
            data-testid={`timeline-iconsonly-milestone-${m.id}`}
          >
            <ProgressIndicatorCircle
              actualDate={m.actual_date}
              isCurrentMilestone={m === currentMilestone}
              data-testid={`timeline-iconsonly-progress-indicator-${m.id}`}
            />
            {length - 1 === index && (
              <div data-testid="timeline-iconsonly-float-block">
                {floatBlock}
              </div>
            )}

            {isOffest && (
              <div
                className="flex items-center"
                data-testid={`timeline-iconsonly-offset-${m.id}`}
              >
                <div
                  className="bl-line"
                  style={{ width: lineWidth }}
                  data-testid={`timeline-iconsonly-offset-line-${m.id}`}
                />
                <div
                  className="bl-rect"
                  data-testid={`timeline-iconsonly-offset-rect-${m.id}`}
                />
              </div>
            )}
            {isNextMileStone && (
              <div
                className="bl-line"
                style={{ width: lineWidth }}
                data-testid={`timeline-iconsonly-next-line-${m.id}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
